import React from 'react';
import { Box, IconButton, Typography, ButtonGroup, Button } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RiZoomInLine, RiZoomOutLine, RiFocusLine } from '@remixicon/react';
import CanvasBoardV2 from './CanvasBoardV2';
import CanvasPropertiesModal from './CanvasPropertiesModal';
import Timeline from './Timeline';
import { setZoom } from '../../redux/slices/canvasSlice';

/**
 * Canvas Board Area - Middle section with artboard
 * 
 * Top-left: "Art board" label with undo/redo
 * Center: Main canvas board for editing
 */
const CanvasBoardArea = ({ showCanvasPropertiesModal, onToggleCanvasProperties }) => {
  const dispatch = useDispatch();
  const { zoom, designMode } = useSelector((state) => state.canvas);

  const handleZoomIn = () => {
    dispatch(setZoom(Math.min(zoom + 0.1, 2))); // Max 200%
  };

  const handleZoomOut = () => {
    dispatch(setZoom(Math.max(zoom - 0.1, 0.1))); // Min 10%
  };

  const handleResetZoom = () => {
    dispatch(setZoom(1)); // Reset to 100%
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#F5F5F5',
      }}
    >
      {/* Top Controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          padding: 2,
        }}
      >
        {/* Art board label */}
        <Typography
          variant="subtitle2"
          onClick={onToggleCanvasProperties}
          sx={{
            cursor: 'pointer',
            fontWeight: 600,
            color: '#4B4A57',
            padding: '6px 12px',
            // backgroundColor: 'white',
            // border: '1px solid #E0E0E0',
            // borderRadius: 1,
            '&:hover': {
              backgroundColor: '#F5F5F5',
            },
          }}
        >
          Art board
        </Typography>

        {/* Zoom Controls */}
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ButtonGroup size="small" variant="outlined">
            <Button onClick={handleZoomOut} disabled={zoom <= 0.1}>
              <RiZoomOutLine size={18} />
            </Button>
            <Button onClick={handleResetZoom} sx={{ minWidth: 70 }}>
              {Math.round(zoom * 100)}%
            </Button>
            <Button onClick={handleZoomIn} disabled={zoom >= 2}>
              <RiZoomInLine size={18} />
            </Button>
          </ButtonGroup>
          <IconButton
            size="small"
            onClick={handleResetZoom}
            sx={{
              backgroundColor: 'white',
              border: '1px solid #E0E0E0',
              '&:hover': { backgroundColor: '#F5F5F5' },
            }}
            title="Fit to screen"
          >
            <RiFocusLine size={18} />
          </IconButton>
        </Box>
      </Box>

      {/* Canvas Board */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          overflow: 'auto',
          padding: 3,
          paddingTop: 1,
          backgroundColor: '#F5F5F5',
          position: 'relative',
        }}
      >
        <CanvasBoardV2 />
      </Box>

      {/* Timeline - Only show in video design mode */}
      {designMode === 'video' && (
        <Box
          sx={{
            borderTop: '1px solid #E0E0E0',
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '300px',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          <Timeline />
        </Box>
      )}

      {/* Canvas Properties Modal */}
      <CanvasPropertiesModal
        open={showCanvasPropertiesModal}
        onClose={onToggleCanvasProperties}
      />
    </Box>
  );
};

export default CanvasBoardArea;

